import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Loader2, LogOut, Save, Upload, Radio, MessageSquare, Phone } from "lucide-react";
import { toast } from "sonner";
import ChatPanel from "@/components/ChatPanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const KEY = "astromeet-astrologer-token";

function getToken() {
  try { const raw = localStorage.getItem(KEY); if (!raw) return null;
    const { token, exp } = JSON.parse(raw);
    if (exp && Date.now() / 1000 > exp) { localStorage.removeItem(KEY); return null; }
    return token;
  } catch { return null; }
}
function saveToken(t, ttl) {
  localStorage.setItem(KEY, JSON.stringify({
    token: t, exp: Math.floor(Date.now() / 1000) + (ttl || 3600),
  }));
}
function clearToken() { localStorage.removeItem(KEY); }
const astroAxios = () => {
  const t = getToken();
  return axios.create({ baseURL: API, headers: t ? { Authorization: `Bearer ${t}` } : {} });
};

export default function AstrologerPortal() {
  const [token, setToken] = useState(getToken());
  if (!token) return <LoginForm onLoggedIn={() => setToken(getToken())} />;
  return <Portal onLogout={() => { clearToken(); setToken(null); }} />;
}

function LoginForm({ onLoggedIn }) {
  const [astroId, setAstroId] = useState("");
  const [pin, setPin] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr("");
    try {
      const r = await axios.post(`${API}/astrologer-portal/login`,
        { astro_id: astroId.trim(), pin: pin.trim() });
      saveToken(r.data.token, r.data.expires_in);
      toast.success(`Welcome, ${r.data.name}`);
      onLoggedIn();
    } catch (ex) {
      setErr(ex?.response?.status === 401 ? "Invalid ID or PIN"
              : (ex?.response?.data?.detail || "Login failed"));
    } finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen bg-[#0A1428] flex items-center justify-center px-4 py-10">
      <form onSubmit={submit} data-testid="astrologer-portal-login"
             className="w-full max-w-sm border border-[#D4AF37]/40 bg-[#0F1A33] p-6 space-y-4 rounded-sm">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-mono">
            Astrologer Portal
          </div>
          <div className="font-heading text-xl text-[#E9E4D0]">Self-service login</div>
        </div>
        <div className="text-xs font-mono text-[#8B99B0]">
          Your admin will share your Astrologer ID (e.g. <span className="text-[#D4AF37]">as_xxxxx</span>)
          and 6-digit PIN when your profile is approved.
        </div>
        <input value={astroId} onChange={(e) => setAstroId(e.target.value)}
                data-testid="astro-id-input" placeholder="as_xxxxxxxxxxxx" required
                className="w-full bg-[#0A1428] border border-[#2A3555] focus:border-[#D4AF37] outline-none
                            text-[#E9E4D0] font-mono text-sm px-3 py-2 rounded-sm" />
        <input value={pin} onChange={(e) => setPin(e.target.value.replace(/[^\d]/g, ""))}
                data-testid="astro-pin-input" placeholder="6-digit PIN" required maxLength={6} type="password"
                className="w-full bg-[#0A1428] border border-[#2A3555] focus:border-[#D4AF37] outline-none
                            text-[#E9E4D0] font-mono text-center text-lg tracking-[0.35em] py-3 rounded-sm" />
        {err && <div className="text-red-400 text-xs font-mono" data-testid="astro-login-error">{err}</div>}
        <button type="submit" disabled={busy || !astroId || pin.length < 4}
                 data-testid="astro-login-submit"
                 className="w-full py-3 rounded-sm bg-gradient-to-r from-[#B08A3E] to-[#E5C158] text-[#0A1428] font-heading disabled:opacity-50">
          {busy ? <><Loader2 size={14} className="inline mr-2 animate-spin" /> Verifying…</> : "Login"}
        </button>
        <Link to="/" className="block text-center text-[10px] font-mono text-[#8B99B0] hover:text-[#D4AF37]">
          ← Back to marketplace
        </Link>
      </form>
    </div>
  );
}

function Portal({ onLogout }) {
  const [me, setMe] = useState(null);
  const [dirty, setDirty] = useState({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const r = await astroAxios().get("/astrologer-portal/me"); setMe(r.data); }
    catch (e) { if (e?.response?.status === 403) onLogout(); }
  };
  useEffect(() => { load(); }, []);

  if (!me) return <div className="min-h-screen bg-[#0A1428] text-[#8B99B0] flex items-center justify-center">Loading…</div>;

  const val = (k) => dirty[k] !== undefined ? dirty[k] : me[k];
  const set = (k, v) => setDirty({ ...dirty, [k]: v });

  const save = async () => {
    setBusy(true);
    try { await astroAxios().patch("/astrologer-portal/me", dirty);
          toast.success("Saved"); setDirty({}); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const toggleOnline = async () => {
    const next = !val("is_online");
    try { await astroAxios().patch("/astrologer-portal/me", { is_online: next });
          toast.success(next ? "You are LIVE" : "You are offline");
          setMe({ ...me, is_online: next });
    } catch (e) { toast.error("Failed"); }
  };

  const uploadPhoto = async (file) => {
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    setBusy(true);
    try { await astroAxios().post("/astrologer-portal/photo", fd,
              { headers: { "Content-Type": "multipart/form-data" } });
          toast.success("Photo updated"); await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#0A1428] text-[#E9E4D0] px-4 sm:px-8 py-6" data-testid="astrologer-dashboard">
      <header className="max-w-4xl mx-auto flex items-center justify-between mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.4em] text-[#D4AF37] font-mono">
            AstroMeet Telugu · Astrologer Portal
          </div>
          <div className="font-heading text-2xl">{me.name}</div>
          <div className="text-[10px] font-mono text-[#8B99B0]">{me.district || me.city}, {me.state}</div>
        </div>
        <div className="flex gap-2">
          <button onClick={toggleOnline} data-testid="astro-toggle-online"
                  className={`rounded-sm px-3 py-2 text-[10px] uppercase tracking-widest font-mono flex items-center gap-1
                    ${me.is_online ? "bg-emerald-500/20 border border-emerald-400 text-emerald-300"
                                   : "border border-[#2A3555] text-[#8B99B0] hover:border-emerald-400/60"}`}>
            <Radio size={12} className={me.is_online ? "animate-pulse" : ""} />
            {me.is_online ? "LIVE · tap to go offline" : "Go LIVE"}
          </button>
          <button onClick={onLogout} className="border border-red-500/40 text-red-400 hover:bg-red-500/10 rounded-sm px-3 py-2 text-[10px] uppercase tracking-widest font-mono flex items-center gap-1">
            <LogOut size={12} /> Logout
          </button>
        </div>
      </header>

      <div className="max-w-4xl mx-auto space-y-4">
        <AstroAnalytics />

        <AstrologerChatRooms />

        <section className="border border-[#2A3555] rounded-sm p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono">Profile Photo</div>
          <div className="flex items-center gap-4">
            <img src={`${API}${me.photo_url}?v=${Date.now()}`}
                  onError={(e) => { e.currentTarget.style.display = "none"; }}
                  alt="You" className="w-24 h-24 object-cover border border-[#D4AF37]/30 rounded-sm" />
            <label className="cursor-pointer border border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A1428] rounded-sm px-4 py-2 text-xs uppercase tracking-widest font-mono flex items-center gap-1">
              <Upload size={12} /> Change photo
              <input type="file" accept="image/*" onChange={(e) => uploadPhoto(e.target.files?.[0])}
                       data-testid="astro-photo-input" className="hidden" />
            </label>
          </div>
        </section>

        <section className="border border-[#2A3555] rounded-sm p-5 space-y-3">
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono">Weekly Slots (free text)</div>
          <input value={val("slots_note") || ""} onChange={(e) => set("slots_note", e.target.value)}
                   data-testid="astro-slots-input"
                   placeholder="e.g. Mon-Sat 10am-8pm IST · WhatsApp for exact slot"
                   className="w-full bg-[#0A1428] border border-[#2A3555] focus:border-[#D4AF37] text-[#E9E4D0] font-mono text-sm px-3 py-2 rounded-sm" />
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono">Short Bio</div>
          <textarea value={val("bio") || ""} onChange={(e) => set("bio", e.target.value)} rows={3} maxLength={800}
                      data-testid="astro-bio-input"
                      className="w-full bg-[#0A1428] border border-[#2A3555] focus:border-[#D4AF37] text-[#E9E4D0] font-mono text-sm px-3 py-2 rounded-sm" />
          <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono">
            Video Room URL (Jitsi) · shown only if admin enabled video
          </div>
          <input value={val("jitsi_room_url") || ""} onChange={(e) => set("jitsi_room_url", e.target.value)}
                   data-testid="astro-jitsi-input"
                   placeholder="https://meet.jit.si/YourRoom (leave blank for auto)"
                   className="w-full bg-[#0A1428] border border-[#2A3555] focus:border-[#D4AF37] text-[#E9E4D0] font-mono text-sm px-3 py-2 rounded-sm" />
          <div className={`text-xs font-mono px-3 py-2 rounded-sm border
              ${me.video_enabled ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/5"
                                     : "border-[#2A3555] text-[#8B99B0]"}`}>
            {me.video_enabled ? "✓ Admin has enabled Video Calls for your profile."
                                    : "Video Calls are OFF — admin must enable them for your card."}
          </div>
          <button onClick={save} disabled={busy || Object.keys(dirty).length === 0}
                    data-testid="astro-save-btn"
                    className="rounded-sm bg-gradient-to-r from-[#B08A3E] to-[#E5C158] text-[#0A1428] font-mono text-xs uppercase tracking-widest px-4 py-2 flex items-center gap-1 disabled:opacity-50">
            <Save size={12} /> {busy ? "Saving…" : "Save changes"}
          </button>
        </section>
      </div>
    </div>
  );
}

function AstroAnalytics() {
  const [s, setS] = useState({ live_minutes: 0, calls_answered: 0, earnings_month: 0 });
  useEffect(() => {
    astroAxios().get("/astrologer-portal/analytics").then(r => setS(r.data)).catch(() => {});
  }, []);
  const tile = (label, value, unit) => (
    <div className="border border-[#D4AF37]/30 rounded-sm p-3 flex-1 text-center">
      <div className="text-[10px] uppercase tracking-widest font-mono text-[#D4AF37]">{label}</div>
      <div className="font-heading text-2xl text-[#E9E4D0] mt-1">{value}<span className="text-xs text-[#8B99B0] ml-1">{unit}</span></div>
    </div>
  );
  return (
    <section data-testid="astro-analytics" className="space-y-2">
      <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono">This Month</div>
      <div className="flex gap-2">
        {tile("Live Minutes", s.live_minutes, "min")}
        {tile("Calls Answered", s.calls_answered, "")}
        {tile("Earnings", s.earnings_month, "₹")}
      </div>
      <div className="text-[10px] font-mono text-[#8B99B0] text-center">
        Live-minutes tick up automatically every time you Go LIVE and then go offline.
        Earnings count only bookings admin marked "completed" this month.
      </div>
    </section>
  );
}

// -----------------------------------------------------------------------
// AstrologerChatRooms — Live Chat tab. Rooms only appear after admin has
// enabled chat on the underlying booking (paid client access).
// -----------------------------------------------------------------------
function AstrologerChatRooms() {
  const [rows, setRows] = useState([]);
  const [active, setActive] = useState(null);
  const token = getToken();

  const load = async () => {
    try { const r = await astroAxios().get("/astrologer-portal/chat-rooms");
          setRows(r.data || []);
    } catch (e) { /* silent */ }
  };
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, []);

  return (
    <section className="border border-[#2A3555] rounded-sm p-5 space-y-3"
             data-testid="astro-chat-tab">
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest text-[#D4AF37] font-mono flex items-center gap-1.5">
          <MessageSquare size={12} /> Live Chat · Paid Clients ({rows.length})
        </div>
        <button onClick={load} className="text-[10px] font-mono text-[#8B99B0] hover:text-[#D4AF37] uppercase tracking-widest">
          Refresh
        </button>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs font-mono text-[#8B99B0] italic">
          No unlocked rooms yet. Admin unlocks Live Chat once a client has paid and been approved.
        </div>
      ) : (
        <div className="space-y-1">
          {rows.map((r) => (
            <div key={r.booking_id}
                  className="grid grid-cols-1 sm:grid-cols-5 gap-2 items-center border border-[#D4AF37]/30 rounded-sm p-3 text-xs">
              <div className="sm:col-span-2 text-[#E9E4D0] font-heading truncate">{r.name}</div>
              <div className="font-mono text-[#D4AF37] uppercase">{r.mode}</div>
              {r.mode === "call" && r.phone && (
                <a href={`tel:${r.phone}`}
                    data-testid={`astro-call-now-${r.booking_id}`}
                    className="border border-emerald-500/60 text-emerald-300 hover:bg-emerald-500/20 rounded-sm px-2 py-1 uppercase tracking-widest font-mono flex items-center justify-center gap-1">
                  <Phone size={10} /> Call Now
                </a>
              )}
              <button onClick={() => setActive(r)}
                        data-testid={`astro-open-chat-${r.booking_id}`}
                        className="border border-[#D4AF37]/60 text-[#D4AF37] hover:bg-[#D4AF37] hover:text-[#0A1428] rounded-sm px-2 py-1 uppercase tracking-widest font-mono">
                Open chat
              </button>
            </div>
          ))}
        </div>
      )}
      {active && (
        <div className="pt-3">
          <ChatPanel bookingId={active.booking_id}
                      title={`You ↔ ${active.name}`}
                      token={token}
                      onClose={() => setActive(null)} />
        </div>
      )}
    </section>
  );
}

